<template>
  <div id="app">
    <MenuBar v-show="$route?.meta?.menuBar"></MenuBar>
    <Splash v-if="showSplash" @hiddenSplash="hiddenSplash"></Splash>
    <!-- Keep-alive only for routes with keepAlive meta - Vue 3 compatible syntax -->
    <router-view v-slot="{ Component, route }">
      <keep-alive v-if="route.meta.keepAlive">
        <component :is="Component" :key="route.path" />
      </keep-alive>
      <component v-else :is="Component" :key="route.path" />
    </router-view>
    <OnScreenDebugger />
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue';
import MenuBar from '@/components/MenuBar.vue';
import Splash from '@/components/Splash.vue';
import OnScreenDebugger from '@/components/OnScreenDebugger.vue';
import { sessionSpend } from '@/utils/mixpanel';
import { preloadCriticalModules } from '@/utils/lazy-load';
import { prewarmCdnOrigins } from '@/utils/cdn-prewarm';
import { memoryMonitor, frameRateMonitor } from '@/config/performance';
import { registerTizenMediaKeys, detectPlatform } from '@/utils/device';
import { initRemoteControl } from './utils/remote-control';
import focusManager from './utils/focus-manager';
import { removeStorage } from './utils/storage';
import { logger } from './utils/dev-logger';
import useUserStore from '@/store/user';
// import { Toast } from './utils/toast';

const showSplash = ref(true);

const hiddenSplash = () => {
  showSplash.value = false;
};

onMounted(async () => {
  sessionSpend();
  logger.log('######################## MOUNTING');

  // Sync persisted user session to Conviva on every app startup.
  // setConvivaUser() is also called on login/logout, but those only fire during the
  // active session — returning users with a stored session would get 'NA' as viewer ID.
  useUserStore().setConvivaUser();
  removeStorage('FOCUS_TARGET_ID');
  // togglePerformanceMonitor();

  // Initialize platform-specific functionality
  const platform = detectPlatform();
  logger.log('########## Detected platform:', platform);

  const supportedPlatforms = ['webos', 'tizen', 'vidaa', 'web'] as const;
  if (supportedPlatforms.includes(platform as any)) {
    initRemoteControl(platform as 'webos' | 'tizen' | 'vidaa' | 'web', focusManager);
  } else {
    // Handle unknown platform - default to web for basic functionality
    logger.warn('Unknown platform detected, defaulting to web mode:', platform);
    initRemoteControl('web', focusManager);
  }

  // Register Tizen media keys if on Tizen platform
  if (platform === 'tizen') {
    try {
      const registered = await registerTizenMediaKeys();
      if (registered) {
        logger.log('Tizen media keys registered successfully');
      } else {
        logger.warn('Failed to register Tizen media keys, FF/RW buttons may not work');
      }
    } catch (error) {
      logger.error('Error registering Tizen media keys:', error);
    }
  }

  // Initialize performance optimizations for TV (development only)
  // Start memory monitoring and frame rate monitoring in development
  if (process.env.NODE_ENV === 'development') {
    // Start frame rate monitoring with less aggressive warnings
    // Only warn if FPS is consistently low (not during initial load)
    let lowFpsCount = 0;
    frameRateMonitor.start(fps => {
      if (fps < 25 && fps > 0) {
        lowFpsCount++;
        // Only warn after 3 consecutive low FPS readings to avoid spam
        if (lowFpsCount >= 3) {
          logger.warn('Performance issue: FPS consistently low at', fps);
          lowFpsCount = 0; // Reset counter
        }
      } else {
        lowFpsCount = 0; // Reset if FPS recovers
      }
    });
  }

  // Preload critical modules in the background
  preloadCriticalModules();

  // Prewarm CDN origins (TCP/TLS handshake) before user navigates to player
  prewarmCdnOrigins();

  // Add keyboard shortcut to toggle performance monitor (Ctrl+Shift+P)
  document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  // Cleanup performance monitors (dev only)
  if (process.env.NODE_ENV === 'development') {
    memoryMonitor.stop();
    frameRateMonitor.stop();
  }

  // Remove event listener
  document.removeEventListener('keydown', handleKeyDown);
});

function handleKeyDown() {
  // Performance monitor removed - no keyboard shortcuts needed
}
</script>

<style>
#app {
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #000;
  width: 100vw;
  height: 100vh;
  position: relative;
  overflow-y: auto;
  /* Optimize scrolling for TV */
  scroll-behavior: auto; /* Instant scrolling for better performance */
  -webkit-overflow-scrolling: touch;
  contain: layout style paint; /* Isolate scroll calculations */
  transform: translateZ(0); /* Force GPU layer */
}

/* Performance Monitor Styles */
.performance-monitor {
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 15px;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  z-index: 9999;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.performance-monitor:hover {
  opacity: 0.9;
}

.monitor-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  min-width: 150px;
}

.monitor-item:last-child {
  margin-bottom: 0;
}

.label {
  color: #ccc;
  margin-right: 10px;
}

.value {
  font-weight: bold;
  color: #fff;
}

.value.Good {
  color: #4ade80;
}

.value.Fair {
  color: #fbbf24;
}

.value.Poor {
  color: #f87171;
}

.value.version {
  color: #00bc6d;
  font-weight: bold;
  background: rgba(0, 188, 109, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid rgba(0, 188, 109, 0.3);
}

.monitor-hint {
  font-size: 11px;
  color: #888;
  text-align: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
